import { useEffect, useState } from "react";
import { Sun, Rss, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/api";
import { useSeo } from "../lib/seo";
import { VerseShare } from "../components/VerseShare";

const Block = ({ label, children, testId }) => (
  <div className="mt-8" data-testid={testId}>
    <div className="overline text-[#d4af37]">{label}</div>
    <div className="mt-3 text-[#1a1a1a] leading-relaxed space-y-4">{children}</div>
  </div>
);

export default function VersePage() {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { api.get("/verse/archive", { params: { limit: 60 } }).then((r) => { setList(r.data); setOpen(r.data[0]?.id || null); }); }, []);
  const today = list?.[0];
  useSeo({
    title: today ? `Verse of the Day — ${today.reference}` : "Verse of the Day",
    description: today ? `${today.reference}: “${today.text}” A deep-dive study with intentional reflection, a daily prompt and prayer.`.slice(0, 160) : "A fresh Bible verse every morning with a deep-dive study, intentional reflection, daily prompt and prayer.",
    path: "/verse",
    image: today ? `${API}/verse/${today.date}/card.png` : null,
  });
  const feedUrl = `${API}/feed/verse.xml`;
  const copyFeed = async () => { try { await navigator.clipboard.writeText(feedUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { toast.info(feedUrl); } };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16" data-testid="verse-page">
      <div className="overline text-[#d4af37] flex items-center gap-2"><Sun className="w-4 h-4" /> Every morning</div>
      <h1 className="font-serif text-5xl mt-3 text-[#0a192f] tracking-tight">Verse of the Day</h1>
      <p className="text-[#4a4a4a] mt-4 max-w-2xl">One verse, one honest deep-dive, one prompt, one prayer — published fresh each morning. Subscribe below to receive it, or follow the feed in your favorite reader.</p>

      {list?.length === 0 && <div className="card-flat p-16 text-center font-serif text-2xl text-[#0a192f] mt-12" data-testid="verse-empty">Today's verse arrives at sunrise.</div>}

      {today && (
        <article className="card-flat mt-12 p-8 lg:p-12 border-l-4 border-[#d4af37]" data-testid="verse-today">
          <div className="text-xs text-[#4a4a4a] tracking-widest uppercase">{new Date(today.date + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</div>
          <blockquote className="font-serif text-3xl lg:text-4xl text-[#0a192f] leading-snug mt-4" data-testid="verse-today-text">“{today.text}”</blockquote>
          <div className="mt-3 text-[#d4af37] font-semibold tracking-wider" data-testid="verse-today-ref">— {today.reference}</div>
          <h2 className="font-serif text-2xl text-[#0a192f] mt-10" data-testid="verse-today-title">{today.title}</h2>
          <Block label="Deep dive" testId="verse-study">{today.study.split(/\n\s*\n/).map((p, i) => <p key={i}>{p}</p>)}</Block>
          <Block label="Intentional reflection" testId="verse-reflection"><p>{today.reflection}</p></Block>
          <Block label="Today's prompt" testId="verse-prompt"><p className="bg-[#fdfbf7] border border-[#d4af37]/40 px-5 py-4 font-medium">{today.prompt}</p></Block>
          <Block label="Prayer" testId="verse-prayer"><p className="italic">{today.prayer}</p></Block>
          <VerseShare study={today} />
        </article>
      )}

      <div className="mt-10 card-flat p-6 flex flex-wrap items-center gap-4" data-testid="verse-feed-box">
        <Rss className="w-5 h-5 text-[#d4af37]" />
        <div className="flex-1 min-w-[200px]">
          <div className="font-semibold text-[#0a192f]">Get it in your inbox or reader</div>
          <div className="text-sm text-[#4a4a4a]">Add this feed to Feedly, Mailbrew, or any RSS-to-email service.</div>
        </div>
        <code className="text-xs bg-[#f4f5f7] px-3 py-2 truncate max-w-full" data-testid="verse-feed-url">{feedUrl}</code>
        <button data-testid="verse-feed-copy" onClick={copyFeed} className="btn-ghost inline-flex items-center gap-2 text-sm">{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} Copy</button>
      </div>

      {list?.length > 1 && (
        <section className="mt-16" data-testid="verse-archive">
          <div className="overline text-[#4a4a4a]">Past days</div>
          <div className="mt-4 divide-y divide-[#e5e5e5] border-t border-b border-[#e5e5e5]">
            {list.slice(1).map((d) => (
              <div key={d.id} id={d.date}>
                <button data-testid={`verse-archive-toggle-${d.date}`} onClick={() => setOpen(open === d.id ? null : d.id)} className="w-full text-left py-5 flex flex-wrap items-baseline gap-x-4">
                  <span className="text-xs text-[#4a4a4a] tracking-widest w-28">{new Date(d.date + "T12:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                  <span className="font-semibold text-[#0a192f]">{d.reference}</span>
                  <span className="text-[#4a4a4a]">{d.title}</span>
                </button>
                {open === d.id && (
                  <div className="pb-8 pl-32 text-[#1a1a1a] leading-relaxed" data-testid={`verse-archive-body-${d.date}`}>
                    <p className="font-serif text-xl text-[#0a192f]">“{d.text}”</p>
                    {d.study.split(/\n\s*\n/).map((p, i) => <p key={i} className="mt-4">{p}</p>)}
                    <p className="mt-4"><strong>Reflection.</strong> {d.reflection}</p>
                    <p className="mt-2"><strong>Prompt.</strong> {d.prompt}</p>
                    <p className="mt-2 italic">{d.prayer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
